import React, { useState, useEffect } from 'react';
import { Button } from 'components/atoms/index';
import Toast from '@/components/atoms/Toast/Toast';
import { useNavigate, useParams } from 'react-router-dom';
import { adminExamResultApi } from '@/services/api';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import ListenButton from '@/components/atoms/ListenButton/ListenButton';

// 재채점 기능 비활성화 (QA 요구 반영)
const ENABLE_RESTART_GRADING = false;

// 관리자 시험 채점 화면
export default function AdminExamResultDetail() {
    const navigate = useNavigate();
    const { examResultId } = useParams<{ examResultId: string }>();
    const [isVisible, setIsVisible] = useState(false);
    const [toastStatus, setToastStatus] = useState<'success' | 'error' | 'info'>('success');
    const [toastMessage, setToastMessage] = useState('');

    const [examName, setExamName] = useState('');
    const [studentName, setStudentName] = useState('');
    const [academyCode, setAcademyCode] = useState('');
    const [academyName, setAcademyName] = useState('');
    const [completionDate, setCompletionDate] = useState('');
    const [gradingStatus, setGradingStatus] = useState('');
    const [timeSpent, setTimeSpent] = useState('');
    const [aiScoring, setAiScoring] = useState<boolean>(false);
    const [items, setItems] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [saving, setSaving] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showRestartModal, setShowRestartModal] = useState(false);
    
    // 채점 데이터 (문항별 점수와 정답 여부)
    const [gradingData, setGradingData] = useState<Record<number, { isCorrect: boolean | null, score: number | null }>>({});
    const [gradingComments, setGradingComments] = useState<Record<number, string>>({});
    const [aiGradedItems, setAiGradedItems] = useState<Record<number, boolean>>({});
    const [aiCommentLoading, setAiCommentLoading] = useState(false);
    const [manualVisible, setManualVisible] = useState<Record<number, boolean>>({});
    
    // 채점 가이드 (관리자 입력)
    const [guideline, setGuideline] = useState({
        objective: '',
        subjective: '',
        essay: ''
    });
    
    const pageSize = 6;

    useEffect(() => {
        if (!examResultId) {
            navigate('/admin/exam-result');
            return;
        }
        loadGradingDetail(parseInt(examResultId, 10));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examResultId]);

    const loadGradingDetail = async (id: number) => {
        try {
            const res: any = await adminExamResultApi.gradingDetail(id);
            const data = res?.data || res;
            
            setExamName(data?.examName || '');
            setStudentName(data?.studentName || '');
            setAcademyName(data?.academyName || data?.academyCode || '');
            setAcademyCode(data?.academyCode || '');
            setCompletionDate(data?.completionDate || '');
            setGradingStatus(data?.gradingStatus || '');
            setTimeSpent(data?.timeSpent || '');
            setAiScoring(!!data?.aiScoring);
            
            const rawItems = Array.isArray(data?.items) ? data.items : [];
            setItems(rawItems);
            const initialManualVisibility = rawItems.reduce((acc: Record<number, boolean>, item: any) => {
                if (!item.studentAnswerItemId) {
                    return acc;
                }
                if (!data?.aiScoring) {
                    acc[item.studentAnswerItemId] = true;
                    return acc;
                }
                acc[item.studentAnswerItemId] = item.isCorrect !== null;
                return acc;
            }, {});
            setManualVisible(initialManualVisibility);
            
            // 기존 채점 데이터 복원
            const initialGrading: Record<number, { isCorrect: boolean | null, score: number | null }> = {};
            const initialAiLocks: Record<number, boolean> = {};
            rawItems.forEach((item: any) => {
                if (item.studentAnswerItemId) {
                    initialGrading[item.studentAnswerItemId] = {
                        isCorrect: item.isCorrect ?? null,
                        score: item.givenScore ?? null
                    };
                    initialAiLocks[item.studentAnswerItemId] = false;
                }
            });
            setGradingData(initialGrading);
            setAiGradedItems(initialAiLocks);

            // 저장된 채점 가이드 불러오기
            if (data?.gradingGuideline) {
                try {
                    const savedGuideline = JSON.parse(data.gradingGuideline);
                    setGuideline({
                        objective: savedGuideline.objective || '',
                        subjective: savedGuideline.subjective || '',
                        essay: savedGuideline.essay || ''
                    });
                    if (savedGuideline.comments && typeof savedGuideline.comments === 'object') {
                        const restoredComments = Object.entries(savedGuideline.comments).reduce((acc, [key, value]) => {
                            const numericKey = Number(key);
                            if (!Number.isNaN(numericKey)) {
                                acc[numericKey] = typeof value === 'string' ? value : '';
                            }
                            return acc;
                        }, {} as Record<number, string>);
                        setGradingComments(restoredComments);
                    } else {
                        setGradingComments({});
                    }
                } catch (e) {
                    console.error('채점 가이드 파싱 실패:', e);
                }
            } else {
                setGradingComments({});
            }
        } catch (e) {
            setToastStatus('error');
            setToastMessage('시험 정보를 불러올 수 없습니다.');
            setIsVisible(true);
        }
    };

    const mapQuestionType = (qt: string): 'multiple' | 'short' | 'essay' => {
        switch ((qt || '').toUpperCase()) {
            case 'MULTIPLE_CHOICE':
                return 'multiple';
            case 'SHORT_ANSWER':
                return 'short';
            case 'ESSAY':
                return 'essay';
            default:
                return 'short';
        }
    };

    const parseChoices = (choices: any): string[] => {
        if (!choices) return [];
        if (Array.isArray(choices)) return choices as string[];
        try {
            const parsed = JSON.parse(choices);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const parseAnswer = (answer: any): any => {
        if (!answer) return null;
        if (typeof answer === 'string') {
            try {
                return JSON.parse(answer);
            } catch {
                return answer;
            }
        }
        return answer;
    };

    const parseStudentAnswer = (answerText: any): any => {
        if (!answerText) return null;
        if (typeof answerText === 'string') {
            try {
                return JSON.parse(answerText);
            } catch {
                return answerText;
            }
        }
        return answerText;
    };

    const toNumberArray = (value: any): number[] => {
        if (value === null || value === undefined) return [];
        const rawList = Array.isArray(value) ? value : [value];
        return rawList
            .map((item) => {
                if (typeof item === 'number') return item;
                if (typeof item === 'string') {
                    const parsed = parseInt(item, 10);
                    return Number.isNaN(parsed) ? null : parsed;
                }
                return null;
            })
            .filter((num): num is number => typeof num === 'number');
    };

    const compareNumberSets = (first: number[], second: number[]) => {
        if (first.length !== second.length) return false;
        const sortedFirst = [...first].sort((a, b) => a - b);
        const sortedSecond = [...second].sort((a, b) => a - b);
        return sortedFirst.every((value, index) => value === sortedSecond[index]);
    };

    const evaluateMultipleAuto = (correctAnswer: any, studentAnswer: any): 'correct' | 'incorrect' | null => {
        const correctIndices = toNumberArray(correctAnswer).map((idx) => idx + 1); // 정답은 0-based
        const studentIndices = toNumberArray(studentAnswer); // 학생 답안은 1-based
        if (!correctIndices.length || !studentIndices.length) return null;
        return compareNumberSets(correctIndices, studentIndices) ? 'correct' : 'incorrect';
    };

    const normalizeText = (value: any) => (value ?? '').toString().trim().toLowerCase();

    const evaluateShortAuto = (correctAnswer: any, studentAnswer: any): 'correct' | 'incorrect' | null => {
        const studentText = normalizeText(studentAnswer);
        if (!studentText) return null;
        const candidates = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
        const isMatch = candidates.some((candidate) => normalizeText(candidate) === studentText);
        return isMatch ? 'correct' : 'incorrect';
    };

    const determineAutoEvaluation = (
        questionType: 'multiple' | 'short' | 'essay',
        correctAnswer: any,
        studentAnswer: any
    ): 'correct' | 'incorrect' | null => {
        if (!studentAnswer) return null;
        if (questionType === 'multiple') {
            return evaluateMultipleAuto(correctAnswer, studentAnswer);
        }
        if (questionType === 'short') {
            return evaluateShortAuto(correctAnswer, studentAnswer);
        }
        return null;
    };

    const handleCommentChange = (resultItemId: number, value: string) => {
        setGradingComments((prev) => ({
            ...prev,
            [resultItemId]: value,
        }));
    };

    // 정답 포맷팅 (0-based index를 1-based로 표시)
    const formatAnswer = (answer: any, choices: string[]): string => {
        if (answer === null || answer === undefined) return '-';
        if (choices.length === 0) return String(answer);
        
        // 배열인 경우 (복수 정답 객관식) - 0-based index를 1-based로 표시
        if (Array.isArray(answer)) {
            const indices = answer.map(a => {
                if (typeof a === 'number') return a;
                if (typeof a === 'string') {
                    const num = parseInt(a, 10);
                    return isNaN(num) ? -1 : num;
                }
                return -1;
            });
            const texts = indices
                .filter(i => i >= 0 && i < choices.length)
                .map(i => `${i + 1}. ${choices[i]}`);
            return texts.length > 0 ? texts.join(', ') : '-';
        }
        
        // 숫자인 경우 (단일 정답 객관식) - 0-based index를 1-based로 표시
        if (typeof answer === 'number') {
            if (answer >= 0 && answer < choices.length) {
                return `${answer + 1}. ${choices[answer]}`;
            }
            return String(answer);
        }
        
        // 문자열인 경우 - 0-based index를 1-based로 표시
        if (typeof answer === 'string') {
            const num = parseInt(answer, 10);
            if (!isNaN(num) && num >= 0 && num < choices.length) {
                return `${num + 1}. ${choices[num]}`;
            }
            return answer;
        }
        
        return String(answer);
    };

    // 학생 답안 포맷팅 (1-based로 저장되어 있으므로 그대로 표시)
    const formatStudentAnswer = (answer: any, choices: string[]): string => {
        if (answer === null || answer === undefined) return '-';
        if (choices.length === 0) return String(answer);
        
        // 배열인 경우 (복수 정답) - 1-based로 저장됨
        if (Array.isArray(answer)) {
            const indices = answer.map(a => {
                if (typeof a === 'number') return a;
                if (typeof a === 'string') {
                    const num = parseInt(a, 10);
                    return isNaN(num) ? 0 : num;
                }
                return 0;
            });
            const texts = indices
                .filter(i => i >= 1 && i <= choices.length)
                .map(i => `${i}. ${choices[i - 1]}`);
            return texts.length > 0 ? texts.join(', ') : '-';
        }
        
        // 숫자인 경우 (단일 정답) - 1-based로 저장됨
        if (typeof answer === 'number') {
            if (answer >= 1 && answer <= choices.length) {
                return `${answer}. ${choices[answer - 1]}`;
            }
            return String(answer);
        }
        
        // 문자열인 경우 - 1-based로 저장됨
        if (typeof answer === 'string') {
            const num = parseInt(answer, 10);
            if (!isNaN(num) && num >= 1 && num <= choices.length) {
                return `${num}. ${choices[num - 1]}`;
            }
            return answer;
        }
        
        return String(answer);
    };

    const handleGradeItem = async (item: any, isCorrect: boolean, score: number) => {
        if (!item.studentAnswerItemId) {
            setToastStatus('error');
            setToastMessage('채점할 답안이 없습니다.');
            setIsVisible(true);
            return;
        }

        try {
            await adminExamResultApi.gradeItem(parseInt(examResultId!, 10), {
                resultItemId: item.studentAnswerItemId,
                isCorrect,
                score
            });

            // 로컬 상태 업데이트
            setGradingData(prev => ({
                ...prev,
                [item.studentAnswerItemId]: { isCorrect, score }
            }));
            setManualVisible(prev => ({
                ...prev,
                [item.studentAnswerItemId]: true,
            }));

            setToastStatus('success');
            setToastMessage('채점이 저장되었습니다.');
            setIsVisible(true);
        } catch (e) {
            setToastStatus('error');
            setToastMessage('채점 저장에 실패했습니다.');
            setIsVisible(true);
        }
    };

    const handleOpenManual = (itemId?: number) => {
        if (!itemId) return;
        setManualVisible((prev) => ({
            ...prev,
            [itemId]: true,
        }));
    };

    const handleAiGrading = async (
        item: any,
        autoEvaluation: 'correct' | 'incorrect' | null,
        maxScore: number,
        absoluteIdx: number,
        context: {
            questionText: string;
            questionType: 'multiple' | 'short' | 'essay';
            correctAnswerText?: string;
            studentAnswerText?: string;
        }
    ) => {
        if (!item.studentAnswerItemId) {
            handleShowToast('error', '자동 채점이 가능한 답안이 없습니다.');
            return;
        }
        if (!autoEvaluation) {
            handleShowToast('error', '자동 판정이 가능한 데이터가 없어 자동 채점을 진행할 수 없습니다.');
            return;
        }
        if (aiGradedItems[item.studentAnswerItemId]) {
            handleShowToast('info', '이미 자동 채점을 완료한 문항입니다.');
            return;
        }

        const isCorrect = autoEvaluation === 'correct';
        const score = isCorrect ? maxScore : 0;

        await handleGradeItem(item, isCorrect, score);
        const {
            questionText,
            questionType,
            correctAnswerText = '',
            studentAnswerText = ''
        } = context;

        const questionLabel = `${absoluteIdx + 1}번`;
        const readableQuestion = questionText || '제시 문항';

        const improvementTip = (() => {
            switch (questionType) {
                case 'multiple':
                    return '보기의 핵심 단서를 다시 확인하고, 지문에서 요구하는 의미와 정확히 일치하는 선택지를 고르는 연습을 해보세요.';
                case 'short':
                    return '정답 표현의 철자와 어순을 다시 점검하고, 질문에서 요구한 핵심 단어를 정확히 넣어보세요.';
                case 'essay':
                    return '문장을 구성할 때 주제에 맞는 근거와 연결어를 명확히 사용해 논리를 세워 보세요.';
                default:
                    return '질문이 요구한 정보를 다시 확인하고, 핵심 단어와 문법을 정확히 적용해보세요.';
            }
        })();

        const autoComment = (() => {
            if (autoEvaluation === 'correct') {
                return `${questionLabel} (${readableQuestion}) 문항을 정확히 이해했습니다. 같은 유형에서도 지금처럼 근거를 확인하면서 답을 선택하면 안정적으로 맞출 수 있습니다.`;
            }
            const lines = [
                `${questionLabel} (${readableQuestion}) 문항에서 학생 답안 "${studentAnswerText || '미응답'}"은 정답 "${correctAnswerText || '-'}"과 일치하지 않습니다.`,
                item.explanation ? `해설: ${item.explanation}` : '',
                `개선 팁: ${improvementTip}`
            ].filter(Boolean);
            return lines.join('\n');
        })();
        setGradingComments((prev) => ({
            ...prev,
            [item.studentAnswerItemId]: autoComment,
        }));
        setAiGradedItems((prev) => ({
            ...prev,
            [item.studentAnswerItemId]: true,
        }));
    };

    const handleGenerateAiGuide = async () => {
        if (!examResultId) {
            handleShowToast('error', '시험 정보를 찾을 수 없습니다.');
            return;
        }
        try {
            setAiCommentLoading(true);
            const response: any = await adminExamResultApi.aiGenerateComments(parseInt(examResultId, 10));
            if (!response?.success) {
                throw new Error(response?.message || 'AI 총평 생성에 실패했습니다.');
            }
            const payload = response?.data || {};
            const typeFeedback = payload?.typeFeedback || {};
            const summary = (payload?.summary || '').trim();

            const composeGuide = (keys: string[]) => {
                const segments = keys
                    .map((key) => (typeFeedback?.[key] || '').trim())
                    .filter(Boolean);
                if (segments.length === 0) {
                    return summary;
                }
                return segments.join('\n');
            };

            setGuideline((prev) => ({
                ...prev,
                objective: composeGuide(['문법', '어휘', '독해']),
                subjective: composeGuide(['문법', '어휘']),
                essay: composeGuide(['쓰기']) || summary,
            }));
            handleShowToast('success', 'AI 총평을 적용했습니다.');
        } catch (error: any) {
            const message = error?.message || 'AI 총평 생성에 실패했습니다.';
            handleShowToast('error', message);
        } finally {
            setAiCommentLoading(false);
        }
    };

    const handleCompleteGrading = () => {
        // 모든 문항이 채점되었는지 확인
        const ungradedItems = items.filter(item => {
            if (!item.studentAnswerItemId) return false;
            const grading = gradingData[item.studentAnswerItemId];
            return !grading || grading.isCorrect === null || grading.score === null;
        });

        if (ungradedItems.length > 0) {
            setToastStatus('error');
            setToastMessage(`아직 채점하지 않은 문항이 ${ungradedItems.length}개 있습니다.`);
            setIsVisible(true);
            return;
        }

        // [QA Fix] 오답 코멘트 필수 검증
        const missingCommentItems = items.filter(item => {
            if (!item.studentAnswerItemId) return false;
            const grading = gradingData[item.studentAnswerItemId];
            if (!grading || grading.isCorrect !== false) return false;
            const commentText = (gradingComments[item.studentAnswerItemId] || '').trim();
            return !commentText;
        });

        if (missingCommentItems.length > 0) {
            setToastStatus('error');
            setToastMessage(`오답 코멘트를 입력하지 않은 문항이 ${missingCommentItems.length}개 있습니다.`);
            setIsVisible(true);
            return;
        }

        // 확인 모달 표시
        setShowCompleteModal(true);
    };

    const confirmCompleteGrading = async () => {
        setShowCompleteModal(false);

        // 총점 계산
        let totalScore = 0;
        let correctCount = 0;
        let wrongCount = 0;

        items.forEach(item => {
            if (item.studentAnswerItemId) {
                const grading = gradingData[item.studentAnswerItemId];
                if (grading) {
                    totalScore += grading.score || 0;
                    if (grading.isCorrect) {
                        correctCount++;
                    } else {
                        wrongCount++;
                    }
                }
            }
        });

        const trimmedComments = Object.entries(gradingComments).reduce((acc, [key, value]) => {
            const trimmed = (value || '').trim();
            if (trimmed) {
                acc[key] = trimmed;
            }
            return acc;
        }, {} as Record<string, string>);

        try {
            setSaving(true);
            await adminExamResultApi.completeGrading(parseInt(examResultId!, 10), {
                totalScore,
                correctCount,
                wrongCount,
                guideline: JSON.stringify({
                    ...guideline,
                    comments: trimmedComments,
                }) // [QA Fix] 채점 가이드 + 오답 코멘트 함께 저장
            });

            setToastStatus('success');
            setToastMessage('채점이 완료되었습니다.');
            setIsVisible(true);
            
            setTimeout(() => {
                navigate('/admin/exam-result');
            }, 1500);
        } catch (e) {
            setToastStatus('error');
            setToastMessage('채점 완료 처리에 실패했습니다.');
            setIsVisible(true);
        } finally {
            setSaving(false);
        }
    };

    const handleShowToast = (status: 'success' | 'error' | 'info', message: string) => {
        setToastStatus(status);
        setToastMessage(message);
        setIsVisible(false);
        setTimeout(() => {
            setIsVisible(true);
        }, 10);
    };

    // 재채점 관련 함수
    const handleRestartGrading = () => {
        setShowRestartModal(true);
    };

    const confirmRestartGrading = async () => {
        setShowRestartModal(false);
        try {
            setSaving(true);
            await adminExamResultApi.restartGrading(parseInt(examResultId!, 10));
            
            setToastStatus('success');
            setToastMessage('재채점을 시작합니다.');
            setIsVisible(true);
            
            // 페이지 새로고침하여 채점 가능한 상태로 변경
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (e) {
            setToastStatus('error');
            setToastMessage('재채점 시작에 실패했습니다.');
            setIsVisible(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <main className='flex flex-1 justify-center w-full bg-white lg:my-10 md:bg-transparent'>
                <div className='flex flex-col pt-8 w-full lg:pt-0'>
                    <div className='flex flex-col gap-2 px-5 py-3 w-full bg-gradient-to-r from-indigo-50 to-purple-50 lg:!bg-none'>
                        <div className='flex justify-between items-center'>
                            <h2 className='text-lg font-bold lg:text-2xl'>
                                {examName}
                            </h2>
                            <Button 
                                size="small" 
                                intent='secondary'
                                onClick={() => navigate('/admin/exam-result')}
                            >
                                목록으로
                            </Button>
                        </div>
                        <div className='flex gap-4 text-sm text-gray-600'>
                            <span>학생: <strong>{studentName}</strong></span>
                            <span>학원: <strong>{academyName || academyCode}</strong></span>
                            <span>응시일: <strong>{completionDate}</strong></span>
                            <span>소요시간: <strong>{timeSpent}</strong></span>
                            <span>채점상태: <strong className='text-blue-600'>{
                                gradingStatus === 'completed' ? '완료' : 
                                gradingStatus === 'in_progress' ? '진행중' : '대기'
                            }</strong></span>
                        </div>
                    </div>

                    {/* 채점 완료 시 성적표 표시 */}
                    {gradingStatus === 'completed' && (() => {
                        // 문항 유형별 통계 계산
                        const hasMultiple = items.some(item => mapQuestionType(item?.questionType) === 'multiple');
                        const hasShort = items.some(item => mapQuestionType(item?.questionType) === 'short');
                        const hasEssay = items.some(item => mapQuestionType(item?.questionType) === 'essay');
                        
                        const multipleItems = items.filter(item => mapQuestionType(item?.questionType) === 'multiple');
                        const shortItems = items.filter(item => mapQuestionType(item?.questionType) === 'short');
                        const essayItems = items.filter(item => mapQuestionType(item?.questionType) === 'essay');
                        
                        const multipleScore = multipleItems.reduce((sum, item) => sum + (item.givenScore || 0), 0);
                        const multipleCorrect = multipleItems.filter(item => item.isCorrect === true).length;
                        const multipleRate = multipleItems.length > 0 ? Math.round((multipleCorrect / multipleItems.length) * 100) : 0;
                        
                        const shortScore = shortItems.reduce((sum, item) => sum + (item.givenScore || 0), 0);
                        const shortCorrect = shortItems.filter(item => item.isCorrect === true).length;
                        const shortRate = shortItems.length > 0 ? Math.round((shortCorrect / shortItems.length) * 100) : 0;
                        
                        const essayScore = essayItems.reduce((sum, item) => sum + (item.givenScore || 0), 0);
                        const essayCorrect = essayItems.filter(item => item.isCorrect === true).length;
                        const essayRate = essayItems.length > 0 ? Math.round((essayCorrect / essayItems.length) * 100) : 0;
                        
                        const wrongMultiple = multipleItems.map((item, idx) => item.givenScore < item.maxScore ? `${idx + 1}번` : '').filter(Boolean).join(', ') || '없음';
                        const wrongShort = shortItems.map((item, idx) => item.givenScore < item.maxScore ? `${idx + 1}번` : '').filter(Boolean).join(', ') || '없음';
                        
                        return (
                            <div className='px-5 pb-6'>
                                {/* 성적표 헤더 */}
                                <div className='px-6 py-6 bg-orange-50 border border-orange-200 rounded-lg mb-6'>
                                    <div className='flex justify-between items-start'>
                                        <div>
                                            <h2 className='text-2xl font-bold text-orange-900 mb-2'>
                                                {studentName}
                                                {(academyName || academyCode) && `(${academyName || academyCode})`} 시험 성적표
                                            </h2>
                                            <p className='text-sm text-gray-600'>{completionDate} 응시일로</p>
                                        </div>
                                        <Button size='small' intent='primary'>채점완료</Button>
                                    </div>
                                </div>
                                
                                {/* 채점 가이드 (읽기 전용) */}
                                <div className='space-y-4 mb-6'>
                                    {hasMultiple && guideline.objective && (
                                        <div className='p-4 bg-white rounded-lg border border-orange-300'>
                                            <h4 className='font-bold text-orange-900 mb-2'>{examName}_객관식 (독점/총점)</h4>
                                            <p className='text-sm text-gray-700 whitespace-pre-wrap'>{guideline.objective}</p>
                                        </div>
                                    )}
                                    {hasShort && guideline.subjective && (
                                        <div className='p-4 bg-white rounded-lg border border-orange-300'>
                                            <h4 className='font-bold text-orange-900 mb-2'>{examName}_주관식 (독점/총점)</h4>
                                            <p className='text-sm text-gray-700 whitespace-pre-wrap'>{guideline.subjective}</p>
                                        </div>
                                    )}
                                    {hasEssay && guideline.essay && (
                                        <div className='p-4 bg-white rounded-lg border border-orange-300'>
                                            <h4 className='font-bold text-orange-900 mb-2'>{examName}_프리라이팅 (독점/총점)</h4>
                                            <p className='text-sm text-gray-700 whitespace-pre-wrap'>{guideline.essay}</p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* 성적 표 & 그래프 */}
                                <div className='grid md:grid-cols-2 gap-6 mb-6'>
                                    {/* 성적 표 */}
                                    <div>
                                        <table className='w-full border-collapse'>
                                            <thead>
                                                <tr className='bg-gray-100'>
                                                    <th className='border border-gray-300 px-4 py-2 text-left text-sm'>구분</th>
                                                    {hasMultiple && <th className='border border-gray-300 px-4 py-2 text-center text-sm'>객관식</th>}
                                                    {hasShort && <th className='border border-gray-300 px-4 py-2 text-center text-sm'>주관식</th>}
                                                    {hasEssay && <th className='border border-gray-300 px-4 py-2 text-center text-sm'>프리라이팅</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className='border border-gray-300 px-4 py-2 font-medium text-sm'>총점</td>
                                                    {hasMultiple && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{multipleScore}점</td>}
                                                    {hasShort && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{shortScore}점</td>}
                                                    {hasEssay && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{essayScore}점</td>}
                                                </tr>
                                                <tr>
                                                    <td className='border border-gray-300 px-4 py-2 font-medium text-sm'>독점</td>
                                                    {hasMultiple && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{multipleCorrect}개</td>}
                                                    {hasShort && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{shortCorrect}개</td>}
                                                    {hasEssay && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{essayCorrect}개</td>}
                                                </tr>
                                                <tr className='bg-orange-50'>
                                                    <td className='border border-gray-300 px-4 py-2 font-bold text-sm'>감점받은 문제</td>
                                                    {hasMultiple && <td className='border border-gray-300 px-4 py-2 text-center font-bold text-sm'>{wrongMultiple}</td>}
                                                    {hasShort && <td className='border border-gray-300 px-4 py-2 text-center font-bold text-sm'>{wrongShort}</td>}
                                                    {hasEssay && <td className='border border-gray-300 px-4 py-2 text-center font-bold text-sm'>상단 코멘트 보기</td>}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* 그래프 */}
                                    <div className='flex items-end justify-around h-64 border border-gray-300 rounded-lg p-4 bg-gray-50'>
                                        {hasMultiple && (
                                            <div className='flex flex-col items-center'>
                                                <div className='w-16 bg-gray-400 rounded-t' style={{ height: `${Math.min(multipleRate, 100)}%`, minHeight: '20px' }}></div>
                                                <p className='text-sm font-semibold mt-2'>{multipleRate}%</p>
                                                <p className='text-xs text-gray-600 mt-1'>객관식</p>
                                            </div>
                                        )}
                                        {hasShort && (
                                            <div className='flex flex-col items-center'>
                                                <div className='w-16 bg-gray-500 rounded-t' style={{ height: `${Math.min(shortRate, 100)}%`, minHeight: '20px' }}></div>
                                                <p className='text-sm font-semibold mt-2'>{shortRate}%</p>
                                                <p className='text-xs text-gray-600 mt-1'>주관식</p>
                                            </div>
                                        )}
                                        {hasEssay && (
                                            <div className='flex flex-col items-center'>
                                                <div className='w-16 bg-gray-800 rounded-t' style={{ height: `${Math.min(essayRate, 100)}%`, minHeight: '20px' }}></div>
                                                <p className='text-sm font-semibold mt-2'>{essayRate}%</p>
                                                <p className='text-xs text-gray-600 mt-1'>프리라이팅</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* 문제 카드 section - 페이징 (6문항/페이지, 좌 3개/우 3개) */}
                    {(() => {
                        const start = (currentPage - 1) * pageSize;
                        const end = start + pageSize;
                        const pageItems = items.slice(start, end);
                        const leftItems = pageItems.slice(0, Math.min(3, pageItems.length));
                        const rightItems = pageItems.slice(Math.min(3, pageItems.length));
                        
                        const renderCard = (item: any, absoluteIdx: number) => {
                            const qType = mapQuestionType(item?.questionType);
                            const choices = parseChoices(item?.choices);
                            const hasImage = !!item?.imageUrl;
                            const hasListen = !!item?.audioUrl;
                            const questionText = `${absoluteIdx + 1}. ${item?.questionText || ''}`;
                            
                            const correctAnswer = parseAnswer(item?.answer);
                            const studentAnswer = parseStudentAnswer(item?.studentAnswer);
                            
                            // 정답: 0-based index로 저장됨
                            const correctAnswerText = qType === 'multiple' 
                                ? formatAnswer(correctAnswer, choices)
                                : (typeof correctAnswer === 'string' ? correctAnswer : JSON.stringify(correctAnswer));
                            
                            // 학생 답안: 1-based로 저장되므로 변환 필요
                            const studentAnswerText = qType === 'multiple'
                                ? formatStudentAnswer(studentAnswer, choices)
                                : (studentAnswer ? String(studentAnswer) : '-');

                            const maxScore = item?.score || 0;
                            const grading = item.studentAnswerItemId ? gradingData[item.studentAnswerItemId] : null;
                            const autoEvaluation = determineAutoEvaluation(qType, correctAnswer, studentAnswer);
                            const commentValue = item.studentAnswerItemId ? (gradingComments[item.studentAnswerItemId] || '') : '';
                            const manualFlag = item.studentAnswerItemId ? manualVisible[item.studentAnswerItemId] : false;
                            const isManualEnabled = !aiScoring || manualFlag;

                            return (
                                <div key={item?.id || absoluteIdx} className='flex flex-col gap-4 p-4 bg-white rounded-lg border border-gray-300'>
                                    {/* 문제 표시 */}
                                    <div>
                                        <h3 className='text-lg font-bold mb-4'>{questionText}</h3>
                                        
                                        {/* 오디오만 있는 경우 */}
                                        {!hasImage && hasListen && item?.audioUrl && (
                                            <div className='mb-4'>
                                                <ListenButton size={36} audioSrc={item.audioUrl} />
                                            </div>
                                        )}
                                        
                                        {/* 이미지와 오디오 */}
                                        {hasImage && item?.imageUrl && (
                                            <div className='flex justify-center mb-4'>
                                                <div className='relative'>
                                                    <img 
                                                        src={item.imageUrl} 
                                                        alt="question" 
                                                        className='max-w-[200px] rounded-lg border border-gray-200' 
                                                    />
                                                    {hasListen && item?.audioUrl && (
                                                        <div className='absolute -top-4 -right-4'>
                                                            <ListenButton size={40} audioSrc={item.audioUrl} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* 객관식 보기 (읽기 전용) */}
                                        {qType === 'multiple' && choices.length > 0 && (
                                            <div className='flex flex-col gap-2 mb-4'>
                                                {choices.map((choice, idx) => (
                                                    <div key={idx} className='flex items-center gap-3 p-2 bg-gray-50 rounded'>
                                                        <span className='flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 text-sm font-semibold'>
                                                            {idx + 1}
                                                        </span>
                                                        <span className='font-medium'>{choice}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* 주관식/서술형 답안 안내 */}
                                        {qType !== 'multiple' && (
                                            <div className='p-3 mb-4 bg-blue-50 rounded border border-blue-200'>
                                                <p className='text-sm text-blue-700'>
                                                    {qType === 'essay' ? '서술형 문항입니다. 아래에서 학생 답안을 확인하세요.' : '주관식 문항입니다. 아래에서 학생 답안을 확인하세요.'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* 정답과 학생 답안 표시 */}
                                    <div className='p-4 bg-gray-50 rounded-lg border border-gray-200'>
                                        {autoEvaluation && (
                                            <div
                                                className={`mb-4 px-3 py-2 rounded-md text-sm font-semibold ${
                                                    autoEvaluation === 'correct'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}
                                            >
                                                {/* [QA Fix] 객관식/주관식 자동 판정 시각화 */}
                                                {autoEvaluation === 'correct' ? '자동 판정: 정답' : '자동 판정: 오답'}
                                            </div>
                                        )}
                                        {autoEvaluation === 'incorrect' && item?.explanation && (
                                            <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded'>
                                                <p className='text-xs font-semibold text-red-700 mb-1'>해답</p>
                                                <p className='text-sm text-red-800 whitespace-pre-wrap'>{item.explanation}</p>
                                            </div>
                                        )}
                                        {qType === 'multiple' ? (
                                            <div className='grid grid-cols-2 gap-4 mb-4'>
                                                <div>
                                                    <p className='text-xs text-gray-500 mb-1'>정답</p>
                                                    <p className='text-sm font-semibold text-green-700'>
                                                        {correctAnswerText}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className='text-xs text-gray-500 mb-1'>학생 답안</p>
                                                    <p className='text-sm font-semibold text-blue-700'>
                                                        {studentAnswerText}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className='space-y-3 mb-4'>
                                                <div>
                                                    <p className='text-xs text-gray-500 mb-2'>정답</p>
                                                    <div className='p-3 bg-green-50 rounded border border-green-200'>
                                                        <p className='text-sm font-medium text-green-800 whitespace-pre-wrap'>
                                                            {correctAnswerText}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className='text-xs text-gray-500 mb-2'>학생 답안</p>
                                                    <div className='p-3 bg-blue-50 rounded border border-blue-200'>
                                                        <p className='text-sm font-medium text-blue-800 whitespace-pre-wrap'>
                                                            {studentAnswerText}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* 채점 입력 영역 (채점 완료 시 숨김) */}
                                        {gradingStatus !== 'completed' && item.studentAnswerItemId && (
                                            <div className='flex flex-col gap-3 pt-3 border-t border-gray-300'>
                                                {aiScoring && (
                                                    <div className='flex flex-wrap gap-2'>
                                                        <Button
                                                            size='small'
                                                            intent='primary'
                                                            disabled={
                                                                !item.studentAnswerItemId ||
                                                                !!aiGradedItems[item.studentAnswerItemId]
                                                            }
                                                            onClick={() => handleAiGrading(
                                                                item,
                                                                autoEvaluation,
                                                                maxScore,
                                                                absoluteIdx,
                                                                {
                                                                    questionText,
                                                                    questionType: qType,
                                                                    correctAnswerText,
                                                                    studentAnswerText
                                                                }
                                                            )}
                                                        >
                                                            🤖 AI 채점
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            intent='secondary'
                                                            disabled={manualVisible[item.studentAnswerItemId] === true}
                                                            onClick={() => handleOpenManual(item.studentAnswerItemId)}
                                                        >
                                                            강사 채점
                                                        </Button>
                                                    </div>
                                                )}
                                                {isManualEnabled ? (
                                                    <>
                                                        <div className='flex gap-4 items-center'>
                                                            <div className='flex gap-2'>
                                                                <button
                                                                    className={`px-4 py-2 rounded-md font-semibold transition ${
                                                                        grading?.isCorrect === true 
                                                                            ? 'bg-green-500 text-white' 
                                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                    }`}
                                                                    onClick={() => handleGradeItem(item, true, maxScore)}
                                                                >
                                                                    ✓ 정답
                                                                </button>
                                                                <button
                                                                    className={`px-4 py-2 rounded-md font-semibold transition ${
                                                                        grading?.isCorrect === false 
                                                                            ? 'bg-red-500 text-white' 
                                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                    }`}
                                                                    onClick={() => handleGradeItem(item, false, 0)}
                                                                >
                                                                    ✗ 오답
                                                                </button>
                                                            </div>
                                                            <div className='flex gap-2 items-center'>
                                                                <span className='text-sm text-gray-600'>부분 점수:</span>
                                                                <input
                                                                    type='number'
                                                                    min='0'
                                                                    max={maxScore}
                                                                    value={grading?.score ?? ''}
                                                                    onChange={(e) => {
                                                                        const score = parseInt(e.target.value, 10);
                                                                        if (!isNaN(score) && score >= 0 && score <= maxScore) {
                                                                            const isCorrect = score === maxScore;
                                                                            handleGradeItem(item, isCorrect, score);
                                                                        }
                                                                    }}
                                                                    className='w-16 px-2 py-1 border border-gray-300 rounded'
                                                                />
                                                                <span className='text-sm text-gray-600'>/ {maxScore}점</span>
                                                            </div>
                                                        </div>
                                                        {grading?.isCorrect === false && (
                                                            <div className='flex flex-col gap-2 pt-2 border-t border-dashed border-red-200'>
                                                                <label className='text-xs font-semibold text-red-700'>
                                                                    오답 코멘트 (필수)
                                                                </label>
                                                                <textarea
                                                                    className='w-full p-3 border border-red-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-400'
                                                                    rows={3}
                                                                    placeholder='오답 사유나 피드백을 입력하세요'
                                                                    value={commentValue}
                                                                    onChange={(e) => handleCommentChange(item.studentAnswerItemId!, e.target.value)}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className='text-xs text-gray-500'>
                                                        강사 채점을 선택하면 수동 채점 도구가 활성화됩니다.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* 채점 완료 시 채점 결과 표시 */}
                                        {gradingStatus === 'completed' && item.studentAnswerItemId && grading && (
                                            <div className='pt-3 border-t border-gray-300'>
                                                <div className='flex items-center gap-2'>
                                                    <span className={`px-3 py-1 rounded-md font-medium ${
                                                        grading.isCorrect === true 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {grading.isCorrect ? '✓ 정답' : '✗ 오답'}
                                                    </span>
                                                    <span className='text-sm text-gray-700'>
                                                        {grading.score}점 / {maxScore}점
                                                    </span>
                                                </div>
                                                {grading.isCorrect === false && commentValue && (
                                                    <div className='mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded'>
                                                        <p className='text-xs font-semibold text-yellow-700 mb-1'>오답 코멘트</p>
                                                        <p className='text-sm text-yellow-800 whitespace-pre-wrap'>{commentValue}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {!item.studentAnswerItemId && (
                                            <div className='pt-3 text-sm text-gray-500 border-t border-gray-300'>
                                                답안 없음
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        };
                        
                        return (
                            <section className='lg:grid lg:grid-cols-2 lg:gap-0 *:py-5 lg:*:pb-10 *:px-5'>
                                <div className='flex flex-col gap-5'>
                                    {leftItems.map((it, i) => renderCard(it, start + i))}
                                </div>
                                <div className='flex flex-col gap-5'>
                                    {rightItems.map((it, i) => renderCard(it, start + 3 + i))}
                                </div>
                            </section>
                        );
                    })()}

                    {/* 페이지 네비게이션 */}
                    <div className='flex flex-wrap gap-2 justify-center p-3'>
                        {Array.from({ length: Math.max(1, Math.ceil(items.length / pageSize)) }).map((_, i) => (
                            <button
                                key={i}
                                className={`px-3 py-1 rounded-full border ${
                                    currentPage === i+1 
                                        ? 'border-indigo-500 text-indigo-600 bg-indigo-50' 
                                        : 'border-gray-300 text-gray-600'
                                }`}
                                onClick={() => setCurrentPage(i+1)}
                            >
                                {i+1}
                            </button>
                        ))}
                    </div>
                    
                    {/* 채점 가이드 입력 영역 (채점 완료 시 숨김) */}
                    {gradingStatus !== 'completed' && (() => {
                        const hasMultiple = items.some(item => mapQuestionType(item?.questionType) === 'multiple');
                        const hasShort = items.some(item => mapQuestionType(item?.questionType) === 'short');
                        const hasEssay = items.some(item => mapQuestionType(item?.questionType) === 'essay');
                        if (!hasMultiple && !hasShort && !hasEssay) return null;

                        return (
                            <div className='px-5 py-6 bg-gray-50'>
                                <div className='flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between'>
                                    <h3 className='text-lg font-bold'>채점 가이드</h3>
                                    {aiScoring && (
                                        <Button
                                            size='small'
                                            intent='primary'
                                            disabled={aiCommentLoading}
                                            onClick={handleGenerateAiGuide}
                                        >
                                            {aiCommentLoading ? '생성 중...' : 'AI 총평 생성'}
                                        </Button>
                                    )}
                                </div>
                                <div className='space-y-4'>
                                    {hasMultiple && (
                                        <div className='p-4 bg-white rounded-lg border border-gray-300'>
                                            <h4 className='font-bold text-orange-900 mb-2'>시험명_객관식 (독점/총점)</h4>
                                            <textarea
                                                className='w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-400'
                                                rows={3}
                                                placeholder='객관식 문항 채점 가이드를 입력하세요'
                                                value={guideline.objective}
                                                onChange={(e) => setGuideline(prev => ({ ...prev, objective: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                    {hasShort && (
                                        <div className='p-4 bg-white rounded-lg border border-gray-300'>
                                            <h4 className='font-bold text-orange-900 mb-2'>시험명_주관식 (독점/총점)</h4>
                                            <textarea
                                                className='w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-400'
                                                rows={3}
                                                placeholder='주관식 문항 채점 가이드를 입력하세요'
                                                value={guideline.subjective}
                                                onChange={(e) => setGuideline(prev => ({ ...prev, subjective: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                    {hasEssay && (
                                        <div className='p-4 bg-white rounded-lg border border-gray-300'>
                                            <h4 className='font-bold text-orange-900 mb-2'>시험명_프리라이팅 (독점/총점)</h4>
                                            <textarea
                                                className='w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-400'
                                                rows={3}
                                                placeholder='프리라이팅 문항 채점 가이드를 입력하세요'
                                                value={guideline.essay}
                                                onChange={(e) => setGuideline(prev => ({ ...prev, essay: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* 채점 완료 버튼 (채점 완료 시 숨김) */}
                    {gradingStatus !== 'completed' && (
                        <div className='flex gap-2 justify-center p-5'>
                            <Button 
                                size="large" 
                                intent='primary' 
                                className='w-full lg:max-w-80'
                                disabled={saving}
                                onClick={handleCompleteGrading}
                            >
                                {saving ? '처리 중...' : '채점 완료'}
                            </Button>
                        </div>
                    )}
                    
                    {/* 재채점 버튼 (채점 완료된 경우에만 표시) */}
                    {ENABLE_RESTART_GRADING && gradingStatus === 'completed' && (
                        <div className='flex gap-2 justify-center p-5'>
                            <Button 
                                size="large" 
                                intent='secondary' 
                                className='w-full lg:max-w-80'
                                disabled={saving}
                                onClick={handleRestartGrading}
                            >
                                {saving ? '처리 중...' : '재채점 시작'}
                            </Button>
                        </div>
                    )}
                </div>
            </main>
            
            <Toast 
                position='top-center'    
                status={toastStatus}
                message={toastMessage}
                isVisible={isVisible}
            />

            {/* 채점 완료 확인 모달 */}
            {showCompleteModal && (
                <div className='fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 backdrop-blur-[1px]'>
                    <div className='bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-xl relative z-[1301]'>
                        <div className='text-center'>
                            <h3 className='text-lg font-bold mb-2'>채점을 완료하시겠습니까?</h3>
                            <p className='text-sm text-gray-600 mb-6'>완료 후 재채점을 통해 다시 수정할 수 있습니다.</p>
                            <div className='flex gap-3 justify-center'>
                                <button
                                    className='px-8 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition'
                                    onClick={() => setShowCompleteModal(false)}
                                >
                                    취소
                                </button>
                                <button
                                    className='px-8 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition'
                                    onClick={confirmCompleteGrading}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 재채점 확인 모달 */}
            {ENABLE_RESTART_GRADING && showRestartModal && (
                <div className='fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 backdrop-blur-[1px]'>
                    <div className='bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-xl relative z-[1301]'>
                        <div className='text-center'>
                            <h3 className='text-lg font-bold mb-2'>재채점을 시작하시겠습니까?</h3>
                            <p className='text-sm text-gray-600 mb-6'>채점 상태가 진행중으로 변경되어 수정이 가능해집니다.</p>
                            <div className='flex gap-3 justify-center'>
                                <button
                                    className='px-8 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition'
                                    onClick={() => setShowRestartModal(false)}
                                >
                                    취소
                                </button>
                                <button
                                    className='px-8 py-2 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 transition'
                                    onClick={confirmRestartGrading}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
